// src/pages/admin/AusenciasFuncoes.js
import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";
import createAutocompleteFilter from "pages/utils/autocompleteFilter";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import {
  Card,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  TextField,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";

// ---- filtros por coluna
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

// ---- persistência de filtros
const FILTERS_KEY = "tbl:ausenciasfuncoes:filters";
const KEEP_KEY = "tbl:ausenciasfuncoes:keep";

const autocompleteFilter = createAutocompleteFilter();

export default function AusenciasFuncoes() {
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  const [data, setData] = useState([]);
  const [funcoes, setFuncoes] = useState([]);

  // “Manter filtros” – por defeito TRUE
  const initKeep = (() => {
    try {
      const v = localStorage.getItem(KEEP_KEY);
      return v === null ? true : JSON.parse(v);
    } catch {
      return true;
    }
  })();
  const [keepFilters, setKeepFilters] = useState(initKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  // ---- load principal
  const carregar = async () => {
    const [rows, err] = await handleApi(api.post("AusenciasFuncoes/SelAusenciasFuncoes", {}));
    if (err) return showAlert(err, "error");
    setData(Array.isArray(rows) ? rows : []);
  };

  // ---- load combos (funções com ausencias = 1)
  const carregarFuncoes = async () => {
    const [f, errF] = await handleApi(api.post("AusenciasFuncoes/SelFuncoesAusenciasCombo", {}));
    if (errF) return showAlert(errF, "error");
    setFuncoes(Array.isArray(f) ? f : []);
  };

  useEffect(() => {
    (async () => {
      await carregar();
      await carregarFuncoes();
    })();
  }, []);

  // ==================== Dialog Inserir ====================
  const [openAdd, setOpenAdd] = useState(false);
  const [novaAssoc, setNovaAssoc] = useState({
    idfuncaobase: null,
    idfuncaogestora: null,
  });

  const funcoesGestoras = useMemo(
    () => funcoes.filter((f) => f.id !== (novaAssoc.idfuncaobase ?? 0)),
    [funcoes, novaAssoc.idfuncaobase]
  );

  const inserir = async () => {
    if (!novaAssoc.idfuncaobase || !novaAssoc.idfuncaogestora) {
      showAlert("Seleciona Função Base e Função Gestora.", "warning");
      return;
    }
    if (novaAssoc.idfuncaobase === novaAssoc.idfuncaogestora) {
      showAlert("A Função Base e a Função Gestora têm de ser diferentes.", "warning");
      return;
    }

    const body = {
      idfuncaobase: Number(novaAssoc.idfuncaobase),
      idfuncaogestora: Number(novaAssoc.idfuncaogestora),
    };

    const [, err, meta] = await handleApi(api.post("AusenciasFuncoes/InsAusenciaFuncao", body));
    if (err) return showAlert(err, "error");
    if (meta?.message) showAlert(meta.message, meta.severity || "success");

    await carregar();
    setOpenAdd(false);
    setNovaAssoc({ idfuncaobase: null, idfuncaogestora: null });
  };

  // ==================== Dialog Confirmar Eliminação ====================
  const [openConfirm, setOpenConfirm] = useState(false);
  const [alvo, setAlvo] = useState(null);

  const pedirConfirmacao = (row) => {
    setAlvo(row);
    setOpenConfirm(true);
  };

  const fecharConfirmacao = () => {
    setOpenConfirm(false);
    setAlvo(null);
  };

  const confirmarEliminar = async () => {
    if (!alvo?.id) return fecharConfirmacao();

    const [, err, meta] = await handleApi(
      api.post("AusenciasFuncoes/DelAusenciaFuncao", { id: alvo.id })
    );
    if (err) showAlert(err, "error");
    else if (meta?.message) showAlert(meta.message, meta.severity || "success");

    await carregar();
    fecharConfirmacao();
  };

  // ==================== Colunas da Tabela ====================
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 72,
        minWidth: 56,
        maxWidth: 96,
        Cell: ({ row }) => {
          const a = row.original;
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                whiteSpace: "nowrap",
              }}
            >
              <IconButton
                color="error"
                size="small"
                disabled={!temPermissao("Menu Administração", "d")}
                onClick={() => pedirConfirmacao(a)}
                title="Eliminar"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      { Header: "Função base", accessor: "funcaobase", Filter: TextFilter },
      { Header: "Função gestora", accessor: "funcaogestora", Filter: TextFilter },
    ],
    [temPermissao]
  );

  const table = useTable(
    {
      columns,
      data,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  // ==================== Persistência Filtros ====================
  useEffect(() => {
    if (!keepFilters && (state.filters?.length || 0) > 0) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}
    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  // ==================== UI ====================
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              {/* <MDTypography variant="h6">Ausências: Gestão de Funções</MDTypography> */}
              <MDBox display="flex" alignItems="center" gap={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepFilters}
                      onChange={(e) => setKeepFilters(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Manter filtros"
                />
                <MDButton
                  color="success"
                  disabled={!temPermissao("Menu Administração", "w")}
                  onClick={() => setOpenAdd(true)}
                >
                  Nova Associação
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox sx={{ height: "68vh", overflow: "auto", borderRadius: 1 }}>
              <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()}>
                      {hg.headers.map((col) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            background: "#fff",
                            textAlign: "left",
                            padding: "6px 8px",
                            fontSize: 12,
                            color: "#7b809a",
                            verticalAlign: "bottom",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                            width: col.id === "acoes" ? col.width : undefined,
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {col.render("Header")}
                            {col.isSorted ? (
                              col.isSortedDesc ? (
                                <span style={{ marginLeft: 6 }}>▼</span>
                              ) : (
                                <span style={{ marginLeft: 6 }}>▲</span>
                              )
                            ) : null}
                          </div>
                          {!col.disableFilters && (
                            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                              {col.canFilter ? col.render("Filter") : null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            style={{
                              padding: "8px 10px",
                              borderTop: "1px solid #eee",
                              fontSize: 12,
                              color: "#1a2b4c",
                              width: cell.column.id === "acoes" ? cell.column.width : undefined,
                              whiteSpace: cell.column.id === "acoes" ? "nowrap" : "normal",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* Dialog Nova Associação */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova associação de funções (ausências)</DialogTitle>
        <DialogContent dividers>
          <MDBox mt={1} display="flex" flexDirection="column" gap={2}>
            <MDBox>
              <div
                style={{
                  fontSize: 12,
                  color: "#7b809a",
                  marginBottom: 4,
                }}
              >
                Função base
              </div>
              <Autocomplete
                options={funcoes}
                filterOptions={autocompleteFilter}
                getOptionLabel={(opt) => opt?.designacao || ""}
                value={funcoes.find((f) => f.id === novaAssoc.idfuncaobase) || null}
                onChange={(_, value) =>
                  setNovaAssoc((prev) => ({
                    ...prev,
                    idfuncaobase: value?.id ?? null,
                    // se a gestora atual for igual, limpa
                    idfuncaogestora:
                      value && prev.idfuncaogestora === value.id ? null : prev.idfuncaogestora,
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Comece a escrever para filtrar..."
                    size="small"
                  />
                )}
              />
            </MDBox>

            <MDBox>
              <div
                style={{
                  fontSize: 12,
                  color: "#7b809a",
                  marginBottom: 4,
                }}
              >
                Função gestora
              </div>
              <Autocomplete
                options={funcoesGestoras}
                filterOptions={autocompleteFilter}
                getOptionLabel={(opt) => opt?.designacao || ""}
                value={funcoesGestoras.find((f) => f.id === novaAssoc.idfuncaogestora) || null}
                onChange={(_, value) =>
                  setNovaAssoc((prev) => ({
                    ...prev,
                    idfuncaogestora: value?.id ?? null,
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Comece a escrever para filtrar..."
                    size="small"
                  />
                )}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setOpenAdd(false)}>
            Cancelar
          </MDButton>
          <MDButton
            color="success"
            onClick={inserir}
            disabled={!temPermissao("Menu Administração", "w")}
          >
            Gravar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmação eliminar */}
      <Dialog open={openConfirm} onClose={fecharConfirmacao}>
        <DialogTitle>Confirmar eliminação</DialogTitle>
        <DialogContent dividers>
          Tem a certeza que pretende remover esta associação de funções para ausências?
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={fecharConfirmacao}>
            Cancelar
          </MDButton>
          <MDButton color="error" onClick={confirmarEliminar}>
            Eliminar
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
